import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { assignmentAPI } from '../services/api';
import Editor from '@monaco-editor/react';
import AIChatAssistant from './AIChatAssistant';
import { FormattedText } from '../utils/formatText';
import ReportIssueButton from './ReportIssueButton';
import { Skeleton, Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
// REMOVED: import useActivityTracking from '../hooks/useActivityTracking';

const TakeCodingAssignment = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [unlockedHints, setUnlockedHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [feedbackByStep, setFeedbackByStep] = useState({}); // Store feedback per step index
  const [showSummary, setShowSummary] = useState(false);
  const [assignmentSummary, setAssignmentSummary] = useState(null);
  const [fetchingFollowUpHint, setFetchingFollowUpHint] = useState(null);
  const [submittingFinal, setSubmittingFinal] = useState(false);
  const [assignmentStarted, setAssignmentStarted] = useState(false);
  const [expandedTab, setExpandedTab] = useState(null); // 'problemStatement' or 'pseudocode'
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [editorTheme, setEditorTheme] = useState(() => {
    return localStorage.getItem('editorTheme') || 'vs-dark';
  });
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('codingAssignmentDarkMode') === 'true';
  });
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [collapsedHints, setCollapsedHints] = useState({}); // Track collapsed state: { stepIndex-hintIndex: boolean }
  const [collapsedFollowUps, setCollapsedFollowUps] = useState({}); // Track collapsed follow-ups: { stepIndex-hintIndex: boolean }

  // Quiz-related state
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [originalQuizQuestions, setOriginalQuizQuestions] = useState([]); // Store all original questions
  const [questionIndices, setQuestionIndices] = useState([]); // Map displayed questions to original indices
  const [previousQuizResults, setPreviousQuizResults] = useState(null); // Store previous attempt results for retake
  const [quizForStep, setQuizForStep] = useState(null);
  const [consoleOutputByStep, setConsoleOutputByStep] = useState({}); // Store console output per step index
  const [running, setRunning] = useState(false); // For Run button state
  const [quizMode, setQuizMode] = useState(null); // 'perStep' or 'atEnd'
  const [showQuizModeModal, setShowQuizModeModal] = useState(false);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('codingAssignmentDarkMode', newDarkMode);
    // Also update editor theme to match
    setEditorTheme(newDarkMode ? 'vs-dark' : 'light');
    localStorage.setItem('editorTheme', newDarkMode ? 'vs-dark' : 'light');
  };

  // REMOVED: Activity tracking disabled
  // useActivityTracking(
  //   'coding_assignment',
  //   assignment?._id,
  //   assignment?.title,
  //   'edit',
  //   {
  //     language: selectedLanguage,
  //     stepCount: assignment?.steps?.length,
  //     currentStep: currentStepIndex + 1
  //   },
  //   !!assignment // Only track when assignment is loaded
  // );

  // Theme colors
  const theme = darkMode ? {
    // Dark mode colors
    background: '#1a202c',
    surface: '#2d3748',
    surfaceHover: '#4a5568',
    text: '#e2e8f0',
    textSecondary: '#a0aec0',
    textMuted: '#718096',
    border: '#4a5568',
    borderLight: '#2d3748',
    primary: '#667eea',
    primaryLight: '#7c3aed',
    success: '#48bb78',
    warning: '#fbbf24',
    error: '#f56565',
    code: '#1a1a1a',
    shadow: 'rgba(0, 0, 0, 0.3)'
  } : {
    // Light mode colors
    background: '#f7fafc',
    surface: '#ffffff',
    surfaceHover: '#f7fafc',
    text: '#1a202c',
    textSecondary: '#2d3748',
    textMuted: '#718096',
    border: '#e2e8f0',
    borderLight: '#edf2f7',
    primary: '#667eea',
    primaryLight: '#7c3aed',
    success: '#48bb78',
    warning: '#fbbf24',
    error: '#e53e3e',
    code: '#f7fafc',
    shadow: 'rgba(0, 0, 0, 0.1)'
  };

  const developerLevelLabels = {
    internDeveloper: { label: 'Intern Developer', icon: '👨‍💻', color: '#48bb78' },
    juniorDeveloper: { label: 'Junior Developer', icon: '💻', color: '#667eea' },
    midLevelDeveloper: { label: 'Mid-Level Developer', icon: '🚀', color: '#fbbf24' },
    seniorDeveloper: { label: 'Senior Developer', icon: '⭐', color: '#f97316' },
    leadDeveloper: { label: 'Lead Developer', icon: '🎖️', color: '#ef4444' }
  };

  // Map language to Monaco editor language key
  const getMonacoLanguage = (lang) => {
    const langMap = {
      'javascript': 'javascript',
      'python': 'python',
      'java': 'java',
      'sql': 'sql',
      'csharp': 'csharp'
    };
    return langMap[lang] || 'javascript';
  };

  // Helper function to clear all localStorage entries for an assignment
  const clearAssignmentLocalStorage = (assignmentId) => {
    const keysToRemove = [];
    // Find all localStorage keys for this assignment
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`assignment-${assignmentId}-`)) {
        keysToRemove.push(key);
      }
    }
    // Remove all found keys
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`Cleared ${keysToRemove.length} localStorage entries for assignment ${assignmentId}`);
  };

  // Helper function to format time spent
  const formatTimeSpent = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const fetchAssignment = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assignmentAPI.getAssignmentByCode(code);
      setAssignment(response.data.assignment);
      setSubmission(response.data.submission);
      
      if (response.data.assignment.languages.length > 0) {
        setSelectedLanguage(response.data.assignment.languages[0]);
      }
      
      // Check if submission exists in backend
      if (response.data.submission) {
        // Submission exists - restore progress
        setAssignmentStarted(true); // Assignment has been started before
        setQuizMode(response.data.submission.quizMode || 'perStep'); // Set quiz mode from submission
        const hints = {};
        response.data.submission.stepSubmissions.forEach((step, idx) => {
          if (step.hintsUnlocked && step.hintsUnlocked.length > 0) {
            hints[idx] = step.hintsUnlocked;
          }
        });
        setUnlockedHints(hints);
        
        // Load assignment summary if exists
        if (response.data.submission.overallSummary) {
          setAssignmentSummary(response.data.submission.overallSummary);
        }
        
        // Set current step to first incomplete step
        const nextIncomplete = response.data.submission.stepSubmissions.findIndex(
          s => !s.isComplete
        );
        if (nextIncomplete !== -1) {
          setCurrentStepIndex(nextIncomplete);
        } else {
          // All steps completed, show the last step
          setCurrentStepIndex(response.data.submission.stepSubmissions.length - 1);
        }
      } else {
        // No submission in backend - clear any stale localStorage data
        // (Admin may have deleted the submission, or this is a fresh start)
        clearAssignmentLocalStorage(response.data.assignment._id);
        // Default to perStep mode for assignments without submission
        setQuizMode('perStep');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assignment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Load code when step changes
  useEffect(() => {
    if (!assignment || currentStepIndex >= assignment.steps.length) return;
    
    const currentStep = assignment.steps[currentStepIndex];
    const storageKey = `assignment-${assignment._id}-step-${currentStepIndex + 1}-${selectedLanguage}`;
    const savedCode = localStorage.getItem(storageKey);
    const repoCode = searchParams.get('repoCode');
    
    // Priority: saved localStorage > previous submission > previous step's code > starter code with repoCode
    if (savedCode) {
      // Use saved code from localStorage
      setCodeInput(savedCode);
    } else if (submission) {
      // Check if there's a submission for this step
      const stepSubmission = submission.stepSubmissions.find(ss => ss.stepNumber === currentStepIndex + 1);
      if (stepSubmission && stepSubmission.code) {
        setCodeInput(stepSubmission.code);
      } else if (currentStepIndex > 0) {
        // Check previous step's submitted code
        const prevStepSubmission = submission.stepSubmissions.find(ss => ss.stepNumber === currentStepIndex);
        if (prevStepSubmission && prevStepSubmission.code) {
          setCodeInput(prevStepSubmission.code); // Carry forward from previous step
        } else {
          const baseCode = currentStep.starterCode[selectedLanguage] || '';
          setCodeInput(repoCode ? `// Repo Code: ${repoCode}\n\n${baseCode}` : baseCode);
        }
      } else {
        const baseCode = currentStep.starterCode[selectedLanguage] || '';
        setCodeInput(repoCode ? `// Repo Code: ${repoCode}\n\n${baseCode}` : baseCode);
      }
    } else {
      const baseCode = currentStep.starterCode[selectedLanguage] || '';
      setCodeInput(repoCode ? `// Repo Code: ${repoCode}\n\n${baseCode}` : baseCode);
    }
  }, [currentStepIndex, assignment, selectedLanguage, submission, searchParams]);

  useEffect(() => {
    // Auto-save code to localStorage
    if (assignment && currentStepIndex < assignment.steps.length) {
      const storageKey = `assignment-${assignment._id}-step-${currentStepIndex + 1}-${selectedLanguage}`;
      localStorage.setItem(storageKey, codeInput);
    }
  }, [codeInput, assignment, currentStepIndex, selectedLanguage]);

  // Clear success messages when navigating between steps, but keep error messages
  useEffect(() => {
    setFeedbackByStep(prev => {
      const updated = { ...prev };
      // Clear all success messages, keep only error messages
      Object.keys(updated).forEach(stepIndex => {
        if (updated[stepIndex] && updated[stepIndex].isComplete) {
          delete updated[stepIndex];
        }
      });
      return updated;
    });
  }, [currentStepIndex]);

  // Get current step's feedback
  const feedbackData = feedbackByStep[currentStepIndex] || null;

  const handleThemeToggle = () => {
    const newTheme = editorTheme === 'vs-dark' ? 'light' : 'vs-dark';
    setEditorTheme(newTheme);
    localStorage.setItem('editorTheme', newTheme);
  };

  const handleLanguageChange = (lang) => {
    if (!assignment) return;
    
    setSelectedLanguage(lang);
    // Code will be loaded by the useEffect that watches currentStepIndex and selectedLanguage
  };

  const handleUnlockHint = async (hintIndex) => {
    try {
      const stepNumber = currentStepIndex + 1;
      const response = await assignmentAPI.unlockHint({
        submissionId: submission?._id,
        stepNumber,
        hintIndex,
        language: selectedLanguage
      });

      // Update unlocked hints (use currentStepIndex as key to match backend data structure)
      const newUnlockedHints = { ...unlockedHints };
      if (!newUnlockedHints[currentStepIndex]) {
        newUnlockedHints[currentStepIndex] = [];
      }
      
      const existingHint = newUnlockedHints[currentStepIndex].find(h => h.hintIndex === hintIndex);
      if (!existingHint) {
        newUnlockedHints[currentStepIndex].push({
          hintIndex,
          text: response.data.hint,
          followUpHints: []
        });
      }
      
      setUnlockedHints(newUnlockedHints);

      // Refresh submission to get updated ID
      if (!submission) {
        const assignmentResponse = await assignmentAPI.getAssignmentByCode(code);
        setSubmission(assignmentResponse.data.submission);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error unlocking hint');
    }
  };

  const handleUnlockFollowUpHint = async (hintIndex) => {
    const hintKey = `${currentStepIndex}-${hintIndex}`;
    try {
      setFetchingFollowUpHint(hintKey);
      const stepNumber = currentStepIndex + 1;
      const response = await assignmentAPI.unlockFollowUpHint({
        submissionId: submission._id,
        stepNumber,
        hintIndex,
        currentCode: codeInput,
        language: selectedLanguage
      });

      // Update unlocked hints (use currentStepIndex as key to match backend data structure)
      const newUnlockedHints = { ...unlockedHints };
      const hintInteraction = newUnlockedHints[currentStepIndex]?.find(h => h.hintIndex === hintIndex);
      
      if (hintInteraction) {
        if (!hintInteraction.followUpHints) {
          hintInteraction.followUpHints = [];
        }
        hintInteraction.followUpHints.push({
          question: response.data.followUpHint,
          pointPenalty: response.data.penalty
        });
      }
      
      setUnlockedHints(newUnlockedHints);
    } catch (err) {
      alert(err.response?.data?.message || 'Error unlocking follow-up hint');
    } finally {
      setFetchingFollowUpHint(null);
    }
  };

  const handleRunCode = async () => {
    if (!codeInput.trim()) {
      alert('Please write some code before running');
      return;
    }

    setRunning(true);

    try {
      const response = await assignmentAPI.runCode({
        code: codeInput,
        language: selectedLanguage,
        problemStatement: assignment.problemStatement,
        stepDescription: currentStep.description
      });

      // Display the output in the console output section
      if (response.data.consoleOutput) {
        setConsoleOutputByStep(prev => ({
          ...prev,
          [currentStepIndex]: response.data.consoleOutput
        }));
      } else {
        setConsoleOutputByStep(prev => ({
          ...prev,
          [currentStepIndex]: 'No output generated'
        }));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error running code');
    } finally {
      setRunning(false);
    }
  };

  const handleSubmitStep = async () => {
    if (!codeInput.trim()) {
      alert('Please write some code before submitting');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await assignmentAPI.submitStep({
        assignmentCode: code,
        stepNumber: currentStepIndex + 1,
        code: codeInput,
        language: selectedLanguage,
        quizMode: quizMode || 'perStep' // Include quiz mode
      });

      setSubmission(response.data.submission);
      // Store feedback for this specific step
      setFeedbackByStep(prev => ({
        ...prev,
        [currentStepIndex]: response.data.stepResult
      }));
      
      // Store console output for this specific step
      if (response.data.stepResult?.aiReview?.consoleOutput) {
        setConsoleOutputByStep(prev => ({
          ...prev,
          [currentStepIndex]: response.data.stepResult.aiReview.consoleOutput
        }));
      }
      
      // Check if quiz is required
      if (response.data.stepResult.isComplete && response.data.stepResult.requiresQuiz) {
        // Only show quiz modal immediately for perStep mode
        const updatedQuizMode = response.data.submission.quizMode || 'perStep';
        
        if (updatedQuizMode === 'perStep') {
          // Show quiz modal immediately
          const questions = response.data.quizQuestions || [];
          setQuizQuestions(questions);
          setOriginalQuizQuestions(questions);
          setQuestionIndices(questions.map((_, idx) => idx)); // All questions initially
          setQuizAnswers(new Array(questions.length).fill(null));
          setQuizForStep(currentStepIndex + 1);
          setQuizResults(null);
          setPreviousQuizResults(null);
          setShowQuizModal(true);
        }
        // For atEnd mode, quiz will be triggered from pending quizzes section
      }
      
      // Show summary only when assignment is actually complete (all steps + all quizzes)
      if (response.data.submission.isCompleted && response.data.submission.overallSummary) {
        setAssignmentSummary(response.data.submission.overallSummary);
        setTimeout(() => setShowSummary(true), 1500); // Show summary after brief delay
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueToNext = () => {
    if (feedbackData?.isComplete && currentStepIndex < assignment.steps.length - 1) {
      // Check if quiz is required and passed (only for perStep mode)
      const currentStepData = submission?.stepSubmissions[currentStepIndex];
      const currentQuizMode = quizMode || 'perStep'; // Default to perStep for older assignments
      if (currentQuizMode === 'perStep' && feedbackData.requiresQuiz && (!currentStepData?.quizResults?.isPassed)) {
        alert('Please complete and pass the quiz before moving to the next step! 🧠');
        return;
      }
      
      setCurrentStepIndex(currentStepIndex + 1);
      // feedbackData will be cleared by useEffect on currentStepIndex change
    }
  };

  const handleSubmitAnyway = () => {
    // Show confirmation modal
    setShowSubmitConfirmation(true);
  };

  const confirmSubmitAnyway = async () => {
    setShowSubmitConfirmation(false);
    setSubmittingFinal(true);
    setError(null);

    try {
      const response = await assignmentAPI.submitAssignmentAnyway({
        assignmentCode: code
      });

      setSubmission(response.data.submission);
      setAssignmentSummary(response.data.submission.overallSummary);
      setShowSummary(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting assignment');
      alert(err.response?.data?.message || 'Error submitting assignment');
    } finally {
      setSubmittingFinal(false);
    }
  };

  const handleQuizAnswerSelect = (questionIndex, optionIndex) => {
    setQuizAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[questionIndex] = optionIndex;
      return newAnswers;
    });
  };

  const handleQuizSubmit = async () => {
    // Check if all answers are selected
    if (quizAnswers.some(a => a === null)) {
      alert('Please answer all questions before submitting');
      return;
    }

    setSubmittingQuiz(true);
    try {
      // Map answers back to original question indices if we're showing only incorrect questions
      const fullAnswers = new Array(originalQuizQuestions.length).fill(null);
      quizAnswers.forEach((answer, idx) => {
        const originalIdx = questionIndices[idx];
        fullAnswers[originalIdx] = answer;
      });
      
      // For questions not being retaken, use the previous correct answers
      if (previousQuizResults && previousQuizResults.results) {
        previousQuizResults.results.forEach((result, idx) => {
          if (result.isCorrect && fullAnswers[idx] === null) {
            fullAnswers[idx] = result.selectedAnswer;
          }
        });
      }

      // Validate no null values before sending
      if (fullAnswers.some(a => a === null || a === undefined)) {
        console.error('Some answers are missing:', fullAnswers);
        alert('Error: Some quiz answers are missing. Please refresh and try again.');
        return;
      }

      const response = await assignmentAPI.submitQuiz({
        submissionId: submission._id,
        stepNumber: quizForStep,
        answers: fullAnswers,
        language: selectedLanguage
      });

      console.log('Quiz submission response received:', {
        passed: response.data.passed,
        assignmentCompleted: response.data.assignmentCompleted,
        remainingQuizzes: response.data.remainingQuizzes,
        totalQuizzes: response.data.totalQuizzes
      });

      setQuizResults(response.data);
      
      // If quiz passed, update submission
      if (response.data.passed) {
        // Refresh submission to get updated data
        const updatedSubmission = await assignmentAPI.getAssignmentByCode(code);
        setSubmission(updatedSubmission.data.submission);
        
        // Check if assignment is now complete
        if (response.data.assignmentCompleted && response.data.overallSummary) {
          setAssignmentSummary(response.data.overallSummary);
          setTimeout(() => {
            setShowQuizModal(false);
            setShowSummary(true);
          }, 1000);
        }
      }
    } catch (err) {
      console.error('Quiz submission error:', err);
      const errorMessage = err.response?.data?.message || 'Error submitting quiz. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleCloseQuizModal = () => {
    // Only allow closing if quiz is passed
    if (quizResults && quizResults.passed) {
      setShowQuizModal(false);
      setQuizQuestions([]);
      setOriginalQuizQuestions([]);
      setQuestionIndices([]);
      setQuizAnswers([]);
      setQuizResults(null);
      setPreviousQuizResults(null);
      setQuizForStep(null);
      
      // For atEnd mode: Check if all quizzes are complete and assignment is finished
      const currentQuizMode = quizMode || 'perStep'; // Default to perStep for older assignments
      if (currentQuizMode === 'atEnd' && quizResults.assignmentCompleted && quizResults.remainingQuizzes === 0) {
        // All quizzes done - show summary
        if (quizResults.overallSummary) {
          setAssignmentSummary(quizResults.overallSummary);
          setTimeout(() => setShowSummary(true), 500);
        }
      } else {
        // For perStep mode: Check if this was the last step AND all quizzes are completed
        const isLastStep = currentStepIndex + 1 === assignment.steps.length;
        if (isLastStep && submission.isCompleted && submission.overallSummary) {
          setAssignmentSummary(submission.overallSummary);
          setTimeout(() => setShowSummary(true), 500);
        }
      }
    }
  };

  const handleRetryQuiz = () => {
    if (!quizResults || !quizResults.results) {
      // Shouldn't happen, but fallback to showing all questions
      setQuizAnswers(new Array(quizQuestions.length).fill(null));
      setQuizResults(null);
      return;
    }

    // Filter to show only incorrect questions
    const incorrectIndices = [];
    const incorrectQuestions = [];
    
    quizResults.results.forEach((result, idx) => {
      if (!result.isCorrect) {
        incorrectIndices.push(idx);
        incorrectQuestions.push(originalQuizQuestions[idx]);
      }
    });

    if (incorrectQuestions.length === 0) {
      // All correct now (shouldn't happen as passed should be true)
      setQuizResults(null);
      return;
    }

    // Save current results for merging answers on retake
    setPreviousQuizResults(quizResults);

    // Update state to show only incorrect questions
    setQuizQuestions(incorrectQuestions);
    setQuestionIndices(incorrectIndices);
    setQuizAnswers(new Array(incorrectQuestions.length).fill(null));
    setQuizResults(null);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: theme.background, padding: '20px', transition: 'background-color 0.3s' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Header skeleton */}
          <div style={{ 
            backgroundColor: theme.surface, 
            padding: '20px 30px', 
            borderRadius: '10px', 
            marginBottom: '20px',
            boxShadow: `0 2px 4px ${theme.shadow}`,
            transition: 'all 0.3s'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                {/* Title */}
                <Skeleton variant="text" width="60%" height={40} sx={{ bgcolor: theme.border, marginBottom: '12px' }} />
                
                {/* Badges and info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                  <Skeleton variant="rectangular" width={150} height={32} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
                  <Skeleton variant="text" width={120} height={24} sx={{ bgcolor: theme.border }} />
                  <Skeleton variant="text" width={140} height={24} sx={{ bgcolor: theme.border }} />
                </div>
              </div>
              
              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <Skeleton variant="rectangular" width={60} height={44} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
                <Skeleton variant="rectangular" width={180} height={44} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
              </div>
            </div>
          </div>

          {/* Language selector skeleton */}
          <div style={{
            backgroundColor: theme.surface,
            padding: '20px 30px',
            borderRadius: '10px',
            marginBottom: '20px',
            boxShadow: `0 2px 4px ${theme.shadow}`,
            transition: 'all 0.3s'
          }}>
            <Skeleton variant="text" width={250} height={24} sx={{ bgcolor: theme.border, marginBottom: '12px' }} />
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <Skeleton variant="rectangular" width={100} height={40} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
              <Skeleton variant="rectangular" width={100} height={40} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
              <Skeleton variant="rectangular" width={100} height={40} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
            </div>
          </div>

          {/* Problem statement skeleton */}
          <div style={{
            backgroundColor: theme.surface,
            padding: '20px 30px',
            borderRadius: '10px',
            marginBottom: '20px',
            boxShadow: `0 2px 4px ${theme.shadow}`,
            border: `2px solid ${theme.primary}`,
            transition: 'all 0.3s'
          }}>
            <Skeleton variant="text" width={250} height={32} sx={{ bgcolor: theme.border, marginBottom: '16px' }} />
            <div style={{ paddingTop: '16px', borderTop: `1px solid ${theme.border}` }}>
              <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: theme.border, marginBottom: '8px' }} />
              <Skeleton variant="text" width="95%" height={20} sx={{ bgcolor: theme.border, marginBottom: '8px' }} />
              <Skeleton variant="text" width="90%" height={20} sx={{ bgcolor: theme.border, marginBottom: '8px' }} />
              <Skeleton variant="text" width="85%" height={20} sx={{ bgcolor: theme.border, marginBottom: '16px' }} />
              
              {/* Pseudocode section */}
              <Skeleton variant="text" width={300} height={32} sx={{ bgcolor: theme.border, marginTop: '20px', marginBottom: '16px' }} />
              <Skeleton variant="rectangular" width="100%" height={200} sx={{ bgcolor: theme.code, borderRadius: '6px' }} />
            </div>
          </div>

          {/* Two-column layout skeleton */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Left column - Instructions */}
            <div style={{
              backgroundColor: theme.surface,
              padding: '20px',
              borderRadius: '10px',
              boxShadow: `0 2px 4px ${theme.shadow}`,
              transition: 'all 0.3s'
            }}>
              <Skeleton variant="text" width={200} height={28} sx={{ bgcolor: theme.border, marginBottom: '16px' }} />
              <Skeleton variant="text" width="100%" height={18} sx={{ bgcolor: theme.border, marginBottom: '8px' }} />
              <Skeleton variant="text" width="95%" height={18} sx={{ bgcolor: theme.border, marginBottom: '8px' }} />
              <Skeleton variant="text" width="90%" height={18} sx={{ bgcolor: theme.border, marginBottom: '20px' }} />
              
              {/* Hints */}
              <Skeleton variant="text" width={150} height={24} sx={{ bgcolor: theme.border, marginBottom: '12px' }} />
              <Skeleton variant="rectangular" width="100%" height={100} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
            </div>

            {/* Right column - Code editor */}
            <div style={{
              backgroundColor: theme.surface,
              padding: '20px',
              borderRadius: '10px',
              boxShadow: `0 2px 4px ${theme.shadow}`,
              transition: 'all 0.3s'
            }}>
              <Skeleton variant="text" width={180} height={28} sx={{ bgcolor: theme.border, marginBottom: '16px' }} />
              <Skeleton variant="rectangular" width="100%" height={400} sx={{ bgcolor: theme.code, borderRadius: '6px', marginBottom: '16px' }} />
              
              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <Skeleton variant="rectangular" width={100} height={40} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
                <Skeleton variant="rectangular" width={120} height={40} sx={{ bgcolor: theme.border, borderRadius: '6px' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: theme.background, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '30px', textAlign: 'center', backgroundColor: theme.surface, borderRadius: '10px', boxShadow: `0 2px 8px ${theme.shadow}` }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <div style={{fontSize: '20px', color: theme.error, marginBottom: '20px' }}>{error}</div>
          <button 
            onClick={() => {
              const repoCode = searchParams.get('repoCode');
              navigate(repoCode ? `/my-code-repos/${repoCode}` : '/my-coding-assignments');
            }}
            style={{
              padding: '12px 24px',
              backgroundColor: theme.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!assignment) return null;

  const currentStep = assignment.steps[currentStepIndex];
  const stepSubmission = submission?.stepSubmissions.find(ss => ss.stepNumber === currentStepIndex + 1);
  const isStepComplete = stepSubmission?.isComplete || false;
  const completedSteps = submission?.stepSubmissions.filter(ss => ss.isComplete).length || 0;
  const levelConfig = developerLevelLabels[assignment.developerLevel];

  // Calculate overall completion percentage
  // Steps contribute 80%, Quizzes contribute 20%
  const calculateCompletionPercentage = () => {
    const totalSteps = assignment.steps.length;
    const stepsCompletionPercentage = (completedSteps / totalSteps) * 80;
    
    // Check if quizzes are required and completed
    const allQuizzesCompleted = submission?.isCompleted || false;
    const quizCompletionPercentage = allQuizzesCompleted ? 20 : 0;
    
    const totalPercentage = Math.round(stepsCompletionPercentage + quizCompletionPercentage);
    return totalPercentage;
  };

  const completionPercentage = calculateCompletionPercentage();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.background, padding: '20px', transition: 'background-color 0.3s' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          backgroundColor: theme.surface, 
          padding: '20px 30px', 
          borderRadius: '10px', 
          marginBottom: '20px',
          boxShadow: `0 2px 4px ${theme.shadow}`,
          transition: 'all 0.3s'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ color: theme.text, margin: '0 0 10px 0' }}>{assignment.title}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <div style={{
                  padding: '6px 12px',
                  backgroundColor: levelConfig.color,
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  {levelConfig.icon} {levelConfig.label}
                </div>
                <div style={{ color: theme.textMuted }}>
                  Complexity: <span style={{ fontWeight: 'bold', color: theme.textSecondary }}>{assignment.complexity}</span>
                </div>
                <div style={{ color: theme.textMuted }}>
                  Est. Time: <span style={{ fontWeight: 'bold', color: theme.textSecondary }}>{assignment.estimatedTime} min</span>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                style={{
                  padding: '10px 20px',
                  backgroundColor: theme.border,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  transition: 'all 0.2s'
                }}
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button
                onClick={() => {
                  const repoCode = searchParams.get('repoCode');
                  navigate(repoCode ? `/my-code-repos/${repoCode}` : '/my-coding-assignments');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: theme.border,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: theme.textSecondary
                }}
              >
                ← Back to Assignments
              </button>
            </div>
          </div>
        </div>

        {/* Introduction Screen - Show before starting */}
        {!assignmentStarted ? (
          <>
            {/* Language Selector */}
            {assignment.languages.length > 1 && (
              <div style={{
                backgroundColor: theme.surface,
                padding: '20px 30px',
                borderRadius: '10px',
                marginBottom: '20px',
                boxShadow: `0 2px 4px ${theme.shadow}`,
                transition: 'all 0.3s'
              }}>
                <h3 style={{ color: theme.textSecondary, marginBottom: '12px', fontSize: '16px' }}>
                  🌐 Choose Your Programming Language
                </h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {assignment.languages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setSelectedLanguage(lang)}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: selectedLanguage === lang ? theme.primary : theme.border,
                        color: selectedLanguage === lang ? 'white' : theme.textSecondary,
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s'
                      }}
                    >
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Problem Statement & Pseudocode Section */}
            {(assignment.problemStatement || (assignment.pseudocodeByLanguage && assignment.pseudocodeByLanguage[selectedLanguage])) && (
              <div style={{
                backgroundColor: theme.surface,
                padding: '20px 30px',
                borderRadius: '10px',
                marginBottom: '20px',
                boxShadow: `0 2px 4px ${theme.shadow}`,
                border: `2px solid ${theme.primary}`,
                transition: 'all 0.3s'
              }}>
                {/* Problem Statement */}
                {assignment.problemStatement && (
                  <>
                    <h3 style={{ 
                      color: theme.warning, 
                      fontSize: '20px', 
                      fontWeight: 'bold',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>📋</span>
                      Problem Statement
                    </h3>
                    
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: `1px solid ${theme.border}`,
                      color: theme.textSecondary,
                      fontSize: '16px',
                      lineHeight: '1.7',
                      marginBottom: assignment.pseudocodeByLanguage && assignment.pseudocodeByLanguage[selectedLanguage] ? '20px' : '0'
                    }}>
                      <FormattedText text={assignment.problemStatement} />
                    </div>
                  </>
                )}

                {/* Pseudocode */}
                {assignment.pseudocodeByLanguage && assignment.pseudocodeByLanguage[selectedLanguage] && (
                  <>
                    <h3 style={{ 
                      color: theme.primary, 
                      fontSize: '20px', 
                      fontWeight: 'bold',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: assignment.problemStatement ? '20px' : '0'
                    }}>
                      <span>🧠</span>
                      Algorithm Pseudocode ({selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)})
                    </h3>
                    
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: `1px solid ${theme.border}`,
                      backgroundColor: theme.code,
                      padding: '16px',
                      borderRadius: '6px',
                      fontFamily: 'Monaco, Menlo, "Courier New", monospace',
                      fontSize: '15px',
                      lineHeight: '1.8',
                      color: theme.textSecondary,
                      whiteSpace: 'pre-wrap',
                      overflowX: 'auto'
                    }}>
                      {assignment.pseudocodeByLanguage[selectedLanguage]}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Start Assignment Button */}
            <div style={{
              backgroundColor: theme.surface,
              padding: '30px',
              borderRadius: '10px',
              marginBottom: '20px',
              boxShadow: `0 2px 4px ${theme.shadow}`,
              textAlign: 'center',
              transition: 'all 0.3s'
            }}>
              <h3 style={{ color: theme.textSecondary, fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
                Ready to Begin?
              </h3>
              <p style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '24px' }}>
                You'll work through {assignment.steps.length} steps to complete this assignment. 
                Each step builds on the previous one, and you'll receive instant feedback on your code.
              </p>
              <button
                onClick={() => setShowQuizModeModal(true)}
                style={{
                  padding: '16px 40px',
                  backgroundColor: theme.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: `0 4px 6px ${theme.shadow}`,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#38a169';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 6px 8px ${theme.shadow}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.success;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 6px ${theme.shadow}`;
                }}
              >
                🚀 Start Assignment
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Progress */}
            {/* Tabs for Problem Statement & Pseudocode */}
            {(assignment.problemStatement || (assignment.pseudocodeByLanguage && assignment.pseudocodeByLanguage[selectedLanguage])) && (
              <div style={{
                backgroundColor: theme.surface,
                borderRadius: '10px',
                marginBottom: '20px',
                boxShadow: `0 2px 4px ${theme.shadow}`,
                border: `1px solid ${theme.border}`,
                overflow: 'hidden'
              }}>
                {/* Tab Headers - Horizontal Layout */}
                <div style={{
                  display: 'flex',
                  borderBottom: `1px solid ${theme.border}`
                }}>
                  {/* Problem Statement Tab Header */}
                  {assignment.problemStatement && (
                    <div 
                      onClick={() => setExpandedTab(expandedTab === 'problemStatement' ? null : 'problemStatement')}
                      style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        padding: '16px 24px',
                        backgroundColor: expandedTab === 'problemStatement' ? theme.surfaceHover : 'transparent',
                        transition: 'all 0.2s',
                        borderRight: (assignment.pseudocodeByLanguage && assignment.pseudocodeByLanguage[selectedLanguage]) ? `1px solid ${theme.border}` : 'none'
                      }}
                    >
                      <div style={{ 
                        color: theme.success, 
                        fontSize: '16px', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <span style={{ fontSize: '20px' }}>📋</span>
                        <span>Problem Statement</span>
                      </div>
                      <span style={{ 
                        fontSize: '16px',
                        color: theme.success,
                        transition: 'transform 0.2s',
                        transform: expandedTab === 'problemStatement' ? 'rotate(90deg)' : 'rotate(0deg)'
                      }}>
                        ▶
                      </span>
                    </div>
                  )}

                  {/* Algorithm Pseudocode Tab Header */}
                  {assignment.pseudocodeByLanguage && assignment.pseudocodeByLanguage[selectedLanguage] && (
                    <div 
                      onClick={() => setExpandedTab(expandedTab === 'pseudocode' ? null : 'pseudocode')}
                      style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        padding: '16px 24px',
                        backgroundColor: expandedTab === 'pseudocode' ? theme.surfaceHover : 'transparent',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ 
                        color: theme.primary, 
                        fontSize: '16px', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <span style={{ fontSize: '20px' }}>🧠</span>
                        <span>Algorithm Pseudocode ({selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)})</span>
                      </div>
                      <span style={{ 
                        fontSize: '16px',
                        color: theme.primary,
                        transition: 'transform 0.2s',
                        transform: expandedTab === 'pseudocode' ? 'rotate(90deg)' : 'rotate(0deg)'
                      }}>
                        ▶
                      </span>
                    </div>
                  )}
                </div>

                {/* Tab Content */}
                {expandedTab === 'problemStatement' && assignment.problemStatement && (
                  <div style={{
                    padding: '20px 24px',
                    backgroundColor: theme.background
                  }}>
                    <FormattedText 
                      text={assignment.problemStatement} 
                      style={{
                        fontSize: '15px',
                        lineHeight: '1.8',
                        color: theme.textSecondary
                      }}
                    />
                  </div>
                )}

                {expandedTab === 'pseudocode' && assignment.pseudocodeByLanguage && assignment.pseudocodeByLanguage[selectedLanguage] && (
                  <div style={{
                    padding: '20px 24px',
                    backgroundColor: theme.background
                  }}>
                    <div style={{
                      backgroundColor: theme.code,
                      padding: '16px',
                      borderRadius: '6px',
                      fontFamily: 'Monaco, Menlo, "Courier New", monospace',
                      fontSize: '14px',
                      lineHeight: '1.8',
                      color: theme.textSecondary,
                      whiteSpace: 'pre-wrap',
                      overflowX: 'auto',
                      border: `1px solid ${theme.border}`
                    }}>
                      {assignment.pseudocodeByLanguage[selectedLanguage]}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step Progress */}
            <div style={{
              backgroundColor: theme.surface,
              padding: '20px 30px',
              borderRadius: '10px',
              marginBottom: '20px',
              boxShadow: `0 2px 4px ${theme.shadow}`,
              transition: 'all 0.3s'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                <span style={{ fontWeight: 'bold', color: theme.textSecondary }}>
                  🔥 Progress: {completedSteps}/{assignment.steps.length} steps complete
                </span>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px' 
                }}>
                  <span style={{ 
                    fontWeight: 'bold', 
                    fontSize: '18px',
                    color: completionPercentage === 100 ? theme.success : theme.primary 
                  }}>
                    {completionPercentage}%
                  </span>
                  {completionPercentage < 100 && completedSteps === assignment.steps.length && (
                    <span style={{ 
                      fontSize: '12px', 
                      color: theme.warning,
                      fontStyle: 'italic'
                    }}>
                      Complete quizzes for 100%
                    </span>
                  )}
                </div>
              </div>
              {/* Progress Bar */}
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: theme.border,
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '15px'
              }}>
                <div style={{
                  width: `${completionPercentage}%`,
                  height: '100%',
                  backgroundColor: completionPercentage === 100 ? theme.success : theme.primary,
                  transition: 'width 0.3s ease',
                  borderRadius: '4px'
                }} />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {assignment.steps.map((step, idx) => {
                  const stepSub = submission?.stepSubmissions.find(ss => ss.stepNumber === idx + 1);
                  const completed = stepSub?.isComplete || false;
                  const quizPassed = stepSub?.quizResults?.isPassed || false;
                  const isCurrent = idx === currentStepIndex;
                  // Allow clicking on completed steps and next incomplete step
                  const nextIncompleteStep = assignment.steps.findIndex((s, i) => {
                    const sub = submission?.stepSubmissions.find(ss => ss.stepNumber === i + 1);
                    return !sub?.isComplete;
                  });
                  const isAccessible = completed || idx <= (nextIncompleteStep === -1 ? assignment.steps.length - 1 : nextIncompleteStep);

                  return (
                    <div
                      key={idx}
                      title={`${step.title}${completed && quizPassed ? ' - Quiz Passed' : completed && !quizPassed ? ' - Quiz Pending' : ''}`}
                      onClick={() => isAccessible && setCurrentStepIndex(idx)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: completed ? theme.success : isCurrent ? theme.primary : !isAccessible ? theme.border : theme.borderLight,
                        color: (completed || isCurrent) ? 'white' : !isAccessible ? theme.textMuted : theme.textSecondary,
                        borderRadius: '6px',
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        opacity: !isAccessible ? 0.5 : 1,
                        cursor: isAccessible ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s ease',
                        border: isCurrent ? `2px solid ${theme.primaryLight}` : '2px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (isAccessible) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = `0 4px 6px ${theme.shadow}`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div>
                        {completed ? (quizPassed ? '✓' : '✓') : !isAccessible ? '🔒' : idx + 1} Step {idx + 1}
                      </div>
                      {completed && feedbackByStep[idx]?.requiresQuiz && (
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                          {quizPassed ? '🧠✓' : '🧠'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pending Quizzes (for atEnd mode) */}
            {(quizMode || 'perStep') === 'atEnd' && assignment && submission && (() => {
              const pendingQuizzes = assignment.steps.filter(step => {
                const hasQuiz = assignment.quizzesByLanguage?.[selectedLanguage]?.[step.stepNumber]?.length > 0;
                const stepSub = submission.stepSubmissions.find(ss => ss.stepNumber === step.stepNumber);
                const isStepComplete = stepSub?.isComplete || false;
                const isQuizPassed = stepSub?.quizResults?.isPassed || false;
                return hasQuiz && isStepComplete && !isQuizPassed;
              });

              if (pendingQuizzes.length > 0) {
                return (
                  <div 
                    data-section="pending-quizzes"
                    style={{
                      backgroundColor: darkMode ? '#3d2f1f' : '#fff3cd',
                      border: `2px solid ${theme.warning}`,
                      padding: '20px',
                      borderRadius: '10px',
                      marginBottom: '20px',
                      boxShadow: `0 2px 4px ${theme.shadow}`,
                      transition: 'all 0.3s'
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                      <span style={{ fontSize: '24px' }}>🧠</span>
                      <span style={{ fontWeight: 'bold', color: theme.warning, fontSize: '16px' }}>
                        Pending Quizzes ({pendingQuizzes.length})
                      </span>
                    </div>
                    <p style={{ color: theme.textSecondary, fontSize: '14px', marginBottom: '15px' }}>
                      Complete all quizzes to finish the assignment:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {pendingQuizzes.map((step) => {
                        const quizQuestions = assignment.quizzesByLanguage?.[selectedLanguage]?.[step.stepNumber] || [];
                        return (
                          <button
                            key={step.stepNumber}
                            onClick={() => {
                              // Load and show quiz for this step
                              setQuizQuestions(quizQuestions);
                              setOriginalQuizQuestions(quizQuestions);
                              setQuestionIndices(quizQuestions.map((_, idx) => idx));
                              setQuizAnswers(new Array(quizQuestions.length).fill(null));
                              setQuizForStep(step.stepNumber);
                              setQuizResults(null);
                              setPreviousQuizResults(null);
                              setShowQuizModal(true);
                            }}
                            style={{
                              padding: '12px 16px',
                              backgroundColor: theme.surface,
                              border: `1px solid ${theme.warning}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = darkMode ? '#2d3748' : '#fef3c7';
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = theme.surface;
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                          >
                            <span style={{ color: theme.text, fontWeight: '600', fontSize: '14px' }}>
                              Step {step.stepNumber}: {step.title}
                            </span>
                            <span style={{ color: theme.warning, fontSize: '14px' }}>
                              {quizQuestions.length} question{quizQuestions.length > 1 ? 's' : ''} →
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
              {/* Step-by-step coding interface continues here... */}
          <div>
            {/* Current Step */}
            <div style={{
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '10px',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ color: '#2d3748', marginBottom: '15px' }}>
                Step {currentStep.stepNumber}: {currentStep.title}
              </h2>
              <p style={{ color: '#4a5568', lineHeight: '1.6', marginBottom: '20px' }}>
                {currentStep.description}
              </p>

              {/* Feedback Display */}
              {feedbackData && !(() => {
                // Hide feedback when on last step with all steps complete and pending quizzes
                if (currentStepIndex === assignment.steps.length - 1 && submission && (quizMode || 'perStep') === 'atEnd' && !submission.isCompleted) {
                  const lastStepSub = submission.stepSubmissions.find(ss => ss.stepNumber === assignment.steps.length);
                  const lastStepComplete = lastStepSub?.isComplete || false;
                  if (lastStepComplete) {
                    // Check if there are pending quizzes
                    const pendingQuizCount = assignment.steps.filter(step => {
                      const hasQuiz = assignment.quizzesByLanguage?.[selectedLanguage]?.[step.stepNumber]?.length > 0;
                      const stepSub = submission.stepSubmissions.find(ss => ss.stepNumber === step.stepNumber);
                      const isStepComplete = stepSub?.isComplete || false;
                      const isQuizPassed = stepSub?.quizResults?.isPassed || false;
                      return hasQuiz && isStepComplete && !isQuizPassed;
                    }).length;
                    return pendingQuizCount > 0;
                  }
                }
                return false;
              })() && (
                <div style={{
                  backgroundColor: feedbackData.isComplete ? '#f0fdf4' : '#fef2f2',
                  border: `2px solid ${feedbackData.isComplete ? '#48bb78' : '#ef4444'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '15px'
                }}>
                  <div style={{ fontSize: '32px', flexShrink: 0 }}>
                    {feedbackData.isComplete ? '✅' : '❌'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2d3748', marginBottom: '8px' }}>
                      {feedbackData.isComplete ? 'Correct!' : 'Not Quite Right'}
                    </div>
                    <p style={{
                      color: theme.textSecondary,
                      fontSize: '14px',
                      lineHeight: '1.6',
                      margin: 0,
                      marginBottom: feedbackData.isComplete ? '12px' : 0
                    }}>
                      {feedbackData.feedback}
                    </p>
                    {feedbackData.isComplete && currentStepIndex < assignment.steps.length - 1 && (
                      <>
                        {(quizMode || 'perStep') === 'perStep' && feedbackData.requiresQuiz && !submission?.stepSubmissions[currentStepIndex]?.quizResults?.isPassed && (
                          <div style={{
                            backgroundColor: darkMode ? '#3d2f1f' : '#fff3cd',
                            border: `1px solid ${theme.warning}`,
                            borderRadius: '6px',
                            padding: '12px',
                            marginTop: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '20px' }}>🧠</span>
                            <span style={{ color: theme.warning, fontSize: '14px', fontWeight: '600' }}>
                              Complete the quiz to unlock the next step!
                            </span>
                          </div>
                        )}
                        {(quizMode || 'perStep') === 'atEnd' && feedbackData.requiresQuiz && !submission?.stepSubmissions[currentStepIndex]?.quizResults?.isPassed && (
                          <div style={{
                            backgroundColor: darkMode ? '#2d3748' : '#e6f9f0',
                            border: `1px solid ${theme.success}`,
                            borderRadius: '6px',
                            padding: '12px',
                            marginTop: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '20px' }}>✅</span>
                            <span style={{ color: theme.success, fontSize: '14px', fontWeight: '600' }}>
                              Step complete! You can take the quiz later or continue to the next step.
                            </span>
                          </div>
                        )}
                        <button
                          onClick={handleContinueToNext}
                          disabled={(quizMode || 'perStep') === 'perStep' && feedbackData.requiresQuiz && !submission?.stepSubmissions[currentStepIndex]?.quizResults?.isPassed}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: ((quizMode || 'perStep') === 'perStep' && feedbackData.requiresQuiz && !submission?.stepSubmissions[currentStepIndex]?.quizResults?.isPassed) 
                              ? theme.border 
                              : theme.success,
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: ((quizMode || 'perStep') === 'perStep' && feedbackData.requiresQuiz && !submission?.stepSubmissions[currentStepIndex]?.quizResults?.isPassed) 
                              ? 'not-allowed' 
                              : 'pointer',
                            marginTop: '8px',
                            opacity: ((quizMode || 'perStep') === 'perStep' && feedbackData.requiresQuiz && !submission?.stepSubmissions[currentStepIndex]?.quizResults?.isPassed) 
                              ? 0.6 
                              : 1,
                            transition: 'all 0.2s'
                          }}
                        >
                          {submission?.stepSubmissions[currentStepIndex]?.quizResults?.isPassed 
                            ? '✓ Continue to Next Step →' 
                            : 'Continue to Next Step →'
                          }
                        </button>
                      </>
                    )}
                    {!feedbackData.isComplete && (
                      <button
                        onClick={() => {
                          // Clear feedback for current step
                          setFeedbackByStep(prev => {
                            const updated = { ...prev };
                            delete updated[currentStepIndex];
                            return updated;
                          });
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: theme.primary,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          marginTop: '8px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Persistent Pending Quizzes Banner (shows even after page reload) */}
              {currentStepIndex === assignment.steps.length - 1 && submission && (quizMode || 'perStep') === 'atEnd' && !submission.isCompleted && (() => {
                // Check if last step is complete
                const lastStepSub = submission.stepSubmissions.find(ss => ss.stepNumber === assignment.steps.length);
                const lastStepComplete = lastStepSub?.isComplete || false;
                
                if (!lastStepComplete) return null;
                
                // Calculate pending quizzes
                const pendingQuizCount = assignment.steps.filter(step => {
                  const hasQuiz = assignment.quizzesByLanguage?.[selectedLanguage]?.[step.stepNumber]?.length > 0;
                  const stepSub = submission.stepSubmissions.find(ss => ss.stepNumber === step.stepNumber);
                  const isStepComplete = stepSub?.isComplete || false;
                  const isQuizPassed = stepSub?.quizResults?.isPassed || false;
                  return hasQuiz && isStepComplete && !isQuizPassed;
                }).length;

                if (pendingQuizCount === 0) return null;

                return (
                  <div style={{
                    backgroundColor: darkMode ? '#3d2f1f' : '#fff3cd',
                    border: `2px solid ${theme.warning}`,
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '24px' }}>🧠</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: theme.warning, fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                          All Steps Complete! ({completionPercentage}% Overall)
                        </div>
                        <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
                          Complete {pendingQuizCount} pending quiz{pendingQuizCount > 1 ? 'zes' : ''} below to finish the assignment and reach 100%.
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        // Scroll to pending quizzes section
                        const pendingSection = document.querySelector('[data-section="pending-quizzes"]');
                        if (pendingSection) {
                          pendingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: theme.warning,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = darkMode ? '#d97706' : '#f59e0b';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme.warning;
                      }}
                    >
                      Go to Pending Quizzes ↓
                    </button>
                  </div>
                );
              })()}

              {/* Assignment Complete Banner (shows when all done) */}
              {currentStepIndex === assignment.steps.length - 1 && submission && submission.isCompleted && (() => {
                return (
                  <div style={{
                    backgroundColor: darkMode ? '#1a3a2a' : '#e6f9f0',
                    border: `2px solid ${theme.success}`,
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '24px' }}>🎉</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: theme.success, fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                        Assignment Complete! (100%)
                      </div>
                      <div style={{ color: theme.textSecondary, fontSize: '14px', marginBottom: '12px' }}>
                        Congratulations! You've successfully completed all steps and quizzes.
                      </div>
                      <button
                        onClick={() => {
                          const repoCode = searchParams.get('repoCode');
                          navigate(repoCode ? `/my-code-repos/${repoCode}` : '/my-coding-assignments');
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: theme.success,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#38a169';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = theme.success;
                        }}
                      >
                        Go to My Assignments →
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Language Selector and Submit Button Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', gap: '15px' }}>
                {/* Language Selector - Left */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  {assignment.languages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => handleLanguageChange(lang)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: selectedLanguage === lang ? theme.primary : theme.border,
                        color: selectedLanguage === lang ? 'white' : theme.textSecondary,
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: selectedLanguage === lang ? 'bold' : 'normal',
                        textTransform: 'uppercase',
                        fontSize: '14px',
                        transition: 'all 0.2s'
                      }}
                    >
                      {lang}
                    </button>
                  ))}
                </div>

                {/* Run & Submit Buttons - Right */}
                {!(isStepComplete && currentStepIndex === assignment.steps.length - 1) && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {/* Run Button */}
                    <button
                      onClick={handleRunCode}
                      disabled={running || !codeInput.trim()}
                      style={{
                        padding: '12px 20px',
                        backgroundColor: running ? theme.border : theme.success,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: running || !codeInput.trim() ? 'not-allowed' : 'pointer',
                        opacity: running || !codeInput.trim() ? 0.6 : 1,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!running && codeInput.trim()) {
                          e.currentTarget.style.backgroundColor = '#38a169';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!running && codeInput.trim()) {
                          e.currentTarget.style.backgroundColor = theme.success;
                        }
                      }}
                    >
                      {running ? '▶️ Running...' : '▶️ Run'}
                    </button>

                    {/* Submit & Verify Button */}
                    <button
                      onClick={handleSubmitStep}
                      disabled={submitting || !codeInput.trim()}
                      style={{
                        padding: '12px 20px',
                        backgroundColor: submitting ? theme.border : theme.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: submitting || !codeInput.trim() ? 'not-allowed' : 'pointer',
                        opacity: submitting || !codeInput.trim() ? 0.6 : 1,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                    >
                      {submitting ? 'Analyzing... ✨' : isStepComplete ? 'Resubmit ↻' : 'Submit & Verify →'}
                    </button>

                    {/* Submit Anyway Option */}
                    {!submission?.isCompleted && (
                      <button
                        onClick={handleSubmitAnyway}
                        disabled={submittingFinal}
                        style={{
                          padding: '12px 20px',
                          backgroundColor: submittingFinal ? theme.border : theme.warning,
                          color: darkMode ? theme.text : 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: submittingFinal ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          opacity: submittingFinal ? 0.6 : 1,
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          if (!submittingFinal) {
                            e.currentTarget.style.backgroundColor = '#f59e0b';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!submittingFinal) {
                            e.currentTarget.style.backgroundColor = theme.warning;
                          }
                        }}
                        title={`Submit with current progress (${completedSteps}/${assignment.steps.length} steps)`}
                      >
                        {submittingFinal ? 'Submitting...' : 'Submit Anyway'}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Code Editor with Monaco */}
              <div style={{
                border: '1px solid #4a5568',
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {/* Theme Toggle Button */}
                <button
                  onClick={handleThemeToggle}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 10,
                    backgroundColor: editorTheme === 'vs-dark' ? '#1e1e1e' : '#f3f3f3',
                    color: editorTheme === 'vs-dark' ? '#ffffff' : '#333333',
                    border: `1px solid ${editorTheme === 'vs-dark' ? '#3e3e3e' : '#cccccc'}`,
                    borderRadius: '6px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  {editorTheme === 'vs-dark' ? '🌙' : '☀️'}
                  {editorTheme === 'vs-dark' ? 'Dark' : 'Light'}
                </button>
                <Editor
                  height="450px"
                  language={getMonacoLanguage(selectedLanguage)}
                  value={codeInput}
                  onMount={(editor) => {
                    // Close chatbot when user focuses on editor
                    editor.onDidFocusEditorText(() => {
                      setIsChatbotOpen(false);
                    });
                  }}
                  onChange={(value) => {
                    setCodeInput(value || '');
                    // Clear feedback only when user modifies correct code
                    // Keep incorrect feedback visible so user can read and think
                    if (feedbackData && feedbackData.isComplete) {
                      setFeedbackByStep(prev => {
                        const updated = { ...prev };
                        delete updated[currentStepIndex];
                        return updated;
                      });
                    }
                  }}
                  theme={editorTheme}
                  options={{
                    minimap: { enabled: true },
                    fontSize: 16,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    wordWrap: 'on',
                    bracketPairColorization: { enabled: true },
                    autoClosingBrackets: 'always',
                    autoClosingQuotes: 'always',
                    autoClosingDelete: 'always',
                    autoIndent: 'full',
                    formatOnPaste: true,
                    formatOnType: true,
                    suggestOnTriggerCharacters: true,
                    quickSuggestions: true,
                    folding: true,
                    foldingHighlight: true,
                    matchBrackets: 'always',
                    autoSurround: 'languageDefined',
                    contextmenu: true,
                    renderLineHighlight: 'all',
                    cursorBlinking: 'smooth',
                    smoothScrolling: true
                  }}
                />
              </div>
              
              {/* Completion Button - Show when last step is complete AND all quizzes are completed */}
              {isStepComplete && currentStepIndex === assignment.steps.length - 1 && !feedbackData && submission?.isCompleted && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  {submission?.overallSummary && (
                    <button
                      onClick={() => setShowSummary(true)}
                      style={{
                        flex: 1,
                        padding: '16px',
                        backgroundColor: theme.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      📊 View Performance Summary
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const repoCode = searchParams.get('repoCode');
                      navigate(repoCode ? `/my-code-repos/${repoCode}` : '/my-coding-assignments');
                    }}
                    style={{
                      flex: 1,
                      padding: '16px',
                      backgroundColor: theme.success,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    🎉 Return to My Assignments →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Hints Panel */}
          <div>
            <div style={{
              backgroundColor: theme.surface,
              padding: '25px',
              borderRadius: '10px',
              boxShadow: `0 2px 4px ${theme.shadow}`,
              position: 'sticky',
              top: '20px',
              transition: 'all 0.3s'
            }}>
              <h3 style={{ color: theme.text, marginBottom: '15px' }}>💡 Hints</h3>
              
              {currentStep.hints.map((hint, hintIdx) => {
                const hintInteraction = unlockedHints[currentStepIndex]?.find(h => h.hintIndex === hintIdx);
                const isUnlocked = !!hintInteraction;

                return (
                  <div key={hintIdx} style={{
                    marginBottom: '15px',
                    padding: '15px',
                    backgroundColor: theme.code,
                    borderRadius: '8px',
                    border: `1px solid ${theme.border}`,
                    transition: 'all 0.3s'
                  }}>
                    {!isUnlocked ? (
                      <button
                        onClick={() => handleUnlockHint(hintIdx)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: theme.warning,
                          color: darkMode ? theme.text : '#1a202c',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          transition: 'all 0.2s'
                        }}
                      >
                        🔓 Unlock Hint {hintIdx + 1}
                      </button>
                    ) : (
                      <>
                        <div style={{
                          padding: '10px',
                          backgroundColor: darkMode ? '#3d2f1f' : '#fff9e6',
                          borderLeft: `4px solid ${theme.warning}`,
                          borderRadius: '4px',
                          marginBottom: '10px',
                          transition: 'all 0.3s',
                          cursor: 'pointer'
                        }}>
                          <div 
                            onClick={() => {
                              const key = `${currentStepIndex}-${hintIdx}`;
                              setCollapsedHints(prev => ({ ...prev, [key]: !prev[key] }));
                            }}
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: collapsedHints[`${currentStepIndex}-${hintIdx}`] ? '0' : '5px'
                            }}
                          >
                            <div style={{ fontSize: '13px', color: theme.warning, fontWeight: 'bold' }}>
                              💡 Hint {hintIdx + 1}
                            </div>
                            <span style={{ fontSize: '18px', color: theme.warning, userSelect: 'none' }}>
                              {collapsedHints[`${currentStepIndex}-${hintIdx}`] ? '▼' : '▲'}
                            </span>
                          </div>
                          {!collapsedHints[`${currentStepIndex}-${hintIdx}`] && (
                            <div style={{ fontSize: '14px', color: theme.textSecondary, lineHeight: '1.5', marginTop: '8px' }}>
                              {hintInteraction.text || hint.text}
                            </div>
                          )}
                        </div>

                        {/* Follow-up hints */}
                        {hintInteraction.followUpHints?.length > 0 && (
                          <div style={{ marginLeft: '0px' }}>
                            <div 
                              onClick={() => {
                                const key = `followup-${currentStepIndex}-${hintIdx}`;
                                setCollapsedFollowUps(prev => ({ ...prev, [key]: !prev[key] }));
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 10px',
                                backgroundColor: darkMode ? '#2a2f4a' : '#e6f7ff',
                                borderRadius: '4px',
                                marginBottom: '5px',
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                border: `1px solid ${theme.primary}40`
                              }}
                            >
                              <span style={{ fontSize: '16px' }}>
                                {collapsedFollowUps[`followup-${currentStepIndex}-${hintIdx}`] ? '▼' : '▲'}
                              </span>
                              <span style={{ fontSize: '13px', color: theme.primary, fontWeight: 'bold' }}>
                                💬 Follow-up Hints ({hintInteraction.followUpHints.length})
                              </span>
                            </div>
                            {!collapsedFollowUps[`followup-${currentStepIndex}-${hintIdx}`] && (
                              <div>
                                {hintInteraction.followUpHints.map((followUp, fIdx) => (
                                  <div key={fIdx} style={{
                                    padding: '10px',
                                    backgroundColor: darkMode ? '#2a2f4a' : '#e6f7ff',
                                    borderLeft: `4px solid ${theme.primary}`,
                                    borderRadius: '4px',
                                    marginBottom: '10px',
                                    marginLeft: '15px',
                                    transition: 'all 0.3s'
                                  }}>
                                    <div style={{ fontSize: '13px', color: theme.primary, marginBottom: '5px', fontWeight: 'bold' }}>
                                      Follow-up {fIdx + 1}
                                    </div>
                                    <div style={{ fontSize: '14px', color: theme.textSecondary, lineHeight: '1.5' }}>
                                      {followUp.question}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Need more help button */}
                        {hint.allowFollowUp && 
                         submission &&
                         (hintInteraction.followUpHints?.length || 0) < hint.maxFollowUps && (
                          <button
                            onClick={() => handleUnlockFollowUpHint(hintIdx)}
                            disabled={fetchingFollowUpHint === `${currentStepIndex}-${hintIdx}`}
                            style={{
                              width: '100%',
                              padding: '10px',
                              backgroundColor: fetchingFollowUpHint === `${currentStepIndex}-${hintIdx}` ? theme.textMuted : theme.primary,
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: fetchingFollowUpHint === `${currentStepIndex}-${hintIdx}` ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              marginTop: '5px',
                              opacity: fetchingFollowUpHint === `${currentStepIndex}-${hintIdx}` ? 0.6 : 1,
                              transition: 'all 0.2s'
                            }}
                          >
                            {fetchingFollowUpHint === `${currentStepIndex}-${hintIdx}` 
                              ? '⏳ Loading...' 
                              : '🤔 Need more help? (Unlock follow-up hint)'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Console Output Section */}
            {consoleOutputByStep[currentStepIndex] && (
              <div style={{
                marginTop: '20px',
                padding: '20px',
                backgroundColor: darkMode ? '#1a1a1a' : '#f8f9fa',
                borderRadius: '8px',
                border: `2px solid ${theme.success}`,
                transition: 'all 0.3s'
              }}>
                <h4 style={{ 
                  color: theme.success, 
                  marginBottom: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📟</span>
                  <span>Console Output</span>
                </h4>
                <pre style={{
                  margin: 0,
                  padding: '15px',
                  backgroundColor: darkMode ? '#0d1117' : '#ffffff',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: theme.text,
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {consoleOutputByStep[currentStepIndex]}
                </pre>
              </div>
            )}
          </div>
        </div>
          </>
        )}

      {/* Assignment Summary Modal */}
      {showSummary && assignmentSummary && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          padding: '20px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: theme.surface,
            borderRadius: '16px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '40px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            transition: 'all 0.3s'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ fontSize: '64px', marginBottom: '15px' }}>🎉</div>
              <h2 style={{ color: theme.text, fontSize: '28px', fontWeight: 'bold', marginBottom: '10px' }}>
                Assignment Complete!
              </h2>
              <p style={{ color: theme.textMuted, fontSize: '16px' }}>
                Here's your performance summary
              </p>
            </div>

            {/* Statistics */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '15px',
              marginBottom: '30px'
            }}>
              <div style={{
                padding: '20px',
                backgroundColor: darkMode ? '#1c4532' : '#f0fdf4',
                borderRadius: '10px',
                textAlign: 'center',
                border: `2px solid ${theme.success}`,
                transition: 'all 0.3s'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.success }}>
                  {assignmentSummary.totalCorrectFirstTry}
                </div>
                <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '5px' }}>
                  First Try Success
                </div>
              </div>
              <div style={{
                padding: '20px',
                backgroundColor: darkMode ? '#4a1616' : '#fef2f2',
                borderRadius: '10px',
                textAlign: 'center',
                border: `2px solid ${theme.error}`,
                transition: 'all 0.3s'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.error }}>
                  {assignmentSummary.totalRequiredCorrections}
                </div>
                <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '5px' }}>
                  Needed Corrections
                </div>
              </div>
              <div style={{
                padding: '20px',
                backgroundColor: darkMode ? '#2a2f4a' : '#eff6ff',
                borderRadius: '10px',
                textAlign: 'center',
                border: `2px solid ${theme.primary}`,
                transition: 'all 0.3s'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.primary }}>
                  {assignmentSummary.totalAttempts}
                </div>
                <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '5px' }}>
                  Total Attempts
                </div>
              </div>
              <div style={{
                padding: '20px',
                backgroundColor: darkMode ? '#2d2a4a' : '#fef9f3',
                borderRadius: '10px',
                textAlign: 'center',
                border: `2px solid ${theme.warning}`,
                transition: 'all 0.3s'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: theme.warning }}>
                  ⏱️ {formatTimeSpent(submission?.timeSpent)}
                </div>
                <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '5px' }}>
                  Time Spent
                </div>
              </div>
            </div>

            {/* Overall Feedback */}
            <div style={{
              backgroundColor: theme.code,
              padding: '20px',
              borderRadius: '10px',
              marginBottom: '25px',
              border: `2px solid ${theme.border}`,
              transition: 'all 0.3s'
            }}>
              <h3 style={{ color: theme.text, fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                📝 Overall Feedback
              </h3>
              <p style={{ color: theme.textSecondary, fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                {assignmentSummary.overallFeedback}
              </p>
            </div>

            {/* Strengths */}
            {assignmentSummary.strengths && assignmentSummary.strengths.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ color: theme.text, fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                  💪 Strengths
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {assignmentSummary.strengths.map((strength, idx) => (
                    <li key={idx} style={{ color: theme.success, fontSize: '14px', marginBottom: '8px' }}>
                      <span style={{ color: theme.textSecondary }}>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Areas for Improvement */}
            {assignmentSummary.areasForImprovement && assignmentSummary.areasForImprovement.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ color: theme.text, fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                  🎯 Areas for Improvement
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {assignmentSummary.areasForImprovement.map((area, idx) => (
                    <li key={idx} style={{ color: theme.warning, fontSize: '14px', marginBottom: '8px' }}>
                      <span style={{ color: theme.textSecondary }}>{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Common Mistakes */}
            {assignmentSummary.commonMistakes && assignmentSummary.commonMistakes.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: theme.text, fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                  ⚠️ Common Mistakes to Avoid
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {assignmentSummary.commonMistakes.map((mistake, idx) => (
                    <li key={idx} style={{ color: theme.error, fontSize: '14px', marginBottom: '8px' }}>
                      <span style={{ color: theme.textSecondary }}>{mistake}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowSummary(false)}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: theme.border,
                  color: theme.textSecondary,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Review Code
              </button>
              <button
                onClick={() => {
                  const repoCode = searchParams.get('repoCode');
                  navigate(repoCode ? `/my-code-repos/${repoCode}` : '/my-coding-assignments');
                }}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: theme.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Return to Assignments →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Assistant */}
      {assignment && (
        <AIChatAssistant 
          assetId={assignment._id}
          chapterId={assignment._id}
          isDarkMode={darkMode}
          isOpen={isChatbotOpen}
          onOpenChange={setIsChatbotOpen}
          assignmentContext={{
            assignmentId: assignment._id,
            submissionId: submission?._id,
            assignmentTitle: assignment.title,
            currentStep: currentStepIndex + 1,
            totalSteps: assignment.steps.length,
            stepTitle: currentStep.title,
            stepDescription: currentStep.description,
            language: selectedLanguage,
            code: codeInput
          }}
        />
      )}

      {/* Submit Anyway Confirmation Modal */}
      {showSubmitConfirmation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowSubmitConfirmation(false)}
        >
          <div
            style={{
              backgroundColor: theme.surface,
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
              transition: 'all 0.3s'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Warning Icon */}
            <div style={{ textAlign: 'center', fontSize: '48px', marginBottom: '20px' }}>
              ⚠️
            </div>

            {/* Title */}
            <h3 style={{
              textAlign: 'center',
              color: theme.text,
              fontSize: '22px',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Submit Assignment Early?
            </h3>

            {/* Message */}
            <div style={{
              backgroundColor: darkMode ? '#3d2f1f' : '#fff3cd',
              border: `1px solid ${theme.warning}`,
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              transition: 'all 0.3s'
            }}>
              <p style={{
                color: theme.warning,
                fontSize: '15px',
                lineHeight: '1.6',
                margin: 0,
                textAlign: 'center'
              }}>
                You have completed <strong>{submission?.stepSubmissions.filter(ss => ss.isComplete).length || 0}</strong> out of <strong>{assignment.steps.length}</strong> steps.
              </p>
            </div>

            <p style={{
              color: theme.textSecondary,
              fontSize: '15px',
              lineHeight: '1.6',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              Are you sure you want to submit now? This will <strong>finalize your submission</strong> and you won't be able to complete the remaining steps.
            </p>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowSubmitConfirmation(false)}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: theme.border,
                  color: theme.textSecondary,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.borderLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.border;
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmitAnyway}
                disabled={submittingFinal}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: submittingFinal ? theme.border : theme.warning,
                  color: darkMode ? theme.text : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: submittingFinal ? 'not-allowed' : 'pointer',
                  opacity: submittingFinal ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!submittingFinal) {
                    e.currentTarget.style.backgroundColor = '#f59e0b';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submittingFinal) {
                    e.currentTarget.style.backgroundColor = theme.warning;
                  }
                }}
              >
                {submittingFinal ? 'Submitting...' : 'Yes, Submit Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      <Modal
        open={showQuizModal}
        onClose={() => {
          if (quizResults && quizResults.passed) {
            handleCloseQuizModal();
          }
        }}
        aria-labelledby="quiz-modal-title"
        disableScrollLock={true}
        BackdropProps={{ timeout: 0 }}
      >
        <Box sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '95%', sm: '90%', md: '700px' },
          maxHeight: '90vh',
          bgcolor: theme.surface,
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          p: 3,
          overflowY: 'auto',
          outline: 'none'
        }}>
          {quizResults && quizResults.passed && (
            <IconButton
              onClick={handleCloseQuizModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'grey.500',
                '&:hover': {
                  color: 'grey.700',
                  bgcolor: 'grey.100'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          )}

          {/* Quiz Header */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>
              {quizResults ? (quizResults.passed ? '✅' : '❌') : '🧠'}
            </div>
            <h3 id="quiz-modal-title" style={{
              color: theme.text,
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              {quizResults 
                ? (quizResults.passed 
                  ? (quizResults.assignmentCompleted && quizResults.remainingQuizzes === 0 ? '🎉 Assignment Complete!' : 'Quiz Passed!') 
                  : 'Quiz Failed')
                : `Step ${quizForStep} Quiz`
              }
            </h3>
            <p style={{
              color: theme.textSecondary,
              fontSize: '15px',
              marginBottom: quizQuestions.length < originalQuizQuestions.length ? '8px' : '0'
            }}>
              {quizResults 
                ? (quizResults.passed && quizResults.remainingQuizzes > 0
                  ? `Score: ${quizResults.correctCount}/${quizResults.totalQuestions} (100%) - ${quizResults.remainingQuizzes} quiz${quizResults.remainingQuizzes > 1 ? 'zes' : ''} remaining`
                  : `Score: ${quizResults.correctCount}/${quizResults.totalQuestions} (${Math.round((quizResults.correctCount / quizResults.totalQuestions) * 100)}%)`)
                : 'Answer all questions correctly to proceed to the next step'
              }
            </p>
            {!quizResults && quizQuestions.length < originalQuizQuestions.length && (
              <div style={{
                backgroundColor: darkMode ? '#3d2f1f' : '#fff3cd',
                border: `1px solid ${theme.warning}`,
                borderRadius: '8px',
                padding: '12px',
                marginTop: '12px',
                display: 'inline-block'
              }}>
                <span style={{ color: theme.warning, fontSize: '14px', fontWeight: '600' }}>
                  🎯 Retaking {quizQuestions.length} incorrect question{quizQuestions.length > 1 ? 's' : ''} out of {originalQuizQuestions.length}
                </span>
              </div>
            )}
          </div>

          {/* Quiz Questions */}
          {!quizResults && quizQuestions.map((question, qIdx) => (
              <div key={qIdx} style={{
                marginBottom: '24px',
                padding: '20px',
                backgroundColor: darkMode ? '#2d3748' : '#f7fafc',
                borderRadius: '8px',
                border: `1px solid ${theme.border}`
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginBottom: '16px'
                }}>
                  <span style={{
                    backgroundColor: theme.primary,
                    color: 'white',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    marginRight: '12px',
                    flexShrink: 0
                  }}>
                    {questionIndices[qIdx] + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      color: theme.text,
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{question.question}</span>
                      <ReportIssueButton
                        sourceType="assignment"
                        sourceId={assignment._id}
                        questionPath={`quizzesByLanguage.${selectedLanguage}.${quizForStep}.${questionIndices[qIdx]}`}
                        questionId={`${assignment._id}-${selectedLanguage}-${quizForStep}-${questionIndices[qIdx]}`}
                        questionIndex={questionIndices[qIdx]}
                        questionData={{
                          question: question.question,
                          options: question.options,
                          correctAnswer: question.correctAnswer,
                          questionType: question.type,
                          explanation: question.explanation,
                          difficulty: question.difficulty,
                          tags: question.tags
                        }}
                        metadata={{
                          sourceName: assignment.title,
                          sourceCode: assignment.code,
                          language: selectedLanguage,
                          stepNumber: quizForStep,
                          questionNumber: qIdx + 1,
                          totalQuestions: quizQuestions?.length || 0,
                          reportedFrom: 'assignment-quiz-taking',
                          timestamp: new Date().toISOString(),
                          groupName: assignment.group?.name,
                          groupId: assignment.group?._id
                        }}
                        size="icon-only"
                      />
                    </div>
                    <div style={{
                      color: theme.textMuted,
                      fontSize: '13px',
                      fontStyle: 'italic'
                    }}>
                      {question.type === 'multiple-choice' || question.type === 'mcq' ? 'Multiple Choice' : 'True/False'}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '40px' }}>
                  {question.options.map((option, optIdx) => (
                    <button
                      key={optIdx}
                      onClick={() => handleQuizAnswerSelect(qIdx, optIdx)}
                      style={{
                        padding: '14px 16px',
                        backgroundColor: quizAnswers[qIdx] === optIdx ? theme.primary : theme.surface,
                        color: quizAnswers[qIdx] === optIdx ? 'white' : theme.text,
                        border: `2px solid ${quizAnswers[qIdx] === optIdx ? theme.primary : theme.border}`,
                        borderRadius: '8px',
                        fontSize: '15px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                        fontWeight: quizAnswers[qIdx] === optIdx ? 'bold' : 'normal'
                      }}
                      onMouseEnter={(e) => {
                        if (quizAnswers[qIdx] !== optIdx) {
                          e.currentTarget.style.backgroundColor = darkMode ? '#2d3748' : '#edf2f7';
                          e.currentTarget.style.borderColor = theme.primary;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (quizAnswers[qIdx] !== optIdx) {
                          e.currentTarget.style.backgroundColor = theme.surface;
                          e.currentTarget.style.borderColor = theme.border;
                        }
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Quiz Results */}
            {quizResults && (
              <div style={{ marginBottom: '24px' }}>
                {/* Remaining Quizzes Banner */}
                {quizResults.passed && !quizResults.assignmentCompleted && quizResults.remainingQuizzes > 0 && (
                  <div style={{
                    marginBottom: '20px',
                    padding: '16px',
                    backgroundColor: darkMode ? '#3d2f1f' : '#fff3cd',
                    border: `2px solid ${theme.warning}`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '24px' }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: theme.warning, fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                        More Quizzes Required
                      </div>
                      <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
                        You still need to complete {quizResults.remainingQuizzes} more quiz{quizResults.remainingQuizzes > 1 ? 'zes' : ''} to finish the assignment. {(quizMode || 'perStep') === 'atEnd' ? 'Check the pending quizzes section below.' : 'Continue to the next step.'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Assignment Complete Banner */}
                {quizResults.passed && quizResults.assignmentCompleted && quizResults.remainingQuizzes === 0 && (
                  <div style={{
                    marginBottom: '20px',
                    padding: '16px',
                    backgroundColor: darkMode ? '#1a3a2a' : '#e6f9f0',
                    border: `2px solid ${theme.success}`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '24px' }}>🎉</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: theme.success, fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                        Congratulations!
                      </div>
                      <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
                        You've successfully completed all quizzes and the assignment!
                      </div>
                    </div>
                  </div>
                )}

                {quizResults.results.map((result, qIdx) => (
                  <div key={qIdx} style={{
                    marginBottom: '20px',
                    padding: '20px',
                    backgroundColor: result.isCorrect 
                      ? (darkMode ? '#1a3a2a' : '#e6f9f0') 
                      : (darkMode ? '#3a1a1a' : '#fcebeb'),
                    borderRadius: '8px',
                    border: `2px solid ${result.isCorrect ? theme.success : theme.error}`
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <span style={{
                        fontSize: '24px',
                        marginRight: '12px'
                      }}>
                        {result.isCorrect ? '✅' : '❌'}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: theme.text,
                          fontSize: '16px',
                          fontWeight: '600',
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>{result.question}</span>
                          <ReportIssueButton
                            sourceType="assignment"
                            sourceId={assignment._id}
                            questionPath={`quizzesByLanguage.${selectedLanguage}.${quizForStep}.${result.questionIndex}`}
                            questionId={`${assignment._id}-${selectedLanguage}-${quizForStep}-${result.questionIndex}`}
                            questionIndex={result.questionIndex}
                            questionData={{
                              question: result.question,
                              options: originalQuizQuestions[result.questionIndex]?.options,
                              correctAnswer: originalQuizQuestions[result.questionIndex]?.correctAnswer,
                              questionType: originalQuizQuestions[result.questionIndex]?.type,
                              explanation: result.explanation,
                              difficulty: originalQuizQuestions[result.questionIndex]?.difficulty,
                              tags: originalQuizQuestions[result.questionIndex]?.tags
                            }}
                            metadata={{
                              sourceName: assignment.title,
                              sourceCode: assignment.code,
                              language: selectedLanguage,
                              stepNumber: quizForStep,
                              questionNumber: result.questionIndex + 1,
                              totalQuestions: originalQuizQuestions?.length || 0,
                              userAnswer: result.selectedAnswer,
                              isCorrect: result.isCorrect,
                              reportedFrom: 'assignment-quiz-results',
                              timestamp: new Date().toISOString(),
                              groupName: assignment.group?.name,
                              groupId: assignment.group?._id
                            }}
                            size="icon-only"
                          />
                        </div>
                        <div style={{
                          color: theme.textSecondary,
                          fontSize: '14px',
                          marginBottom: '4px'
                        }}>
                          <strong>Your answer:</strong> {result.selectedText || 'No answer'}
                        </div>
                        {!result.isCorrect && (
                          <div style={{
                            color: theme.success,
                            fontSize: '14px',
                            marginBottom: '8px'
                          }}>
                            <strong>Correct answer:</strong> {result.correctText}
                          </div>
                        )}
                        <div style={{
                          color: theme.textSecondary,
                          fontSize: '14px',
                          marginTop: '12px',
                          padding: '12px',
                          backgroundColor: darkMode ? '#2d3748' : 'white',
                          borderRadius: '6px',
                          borderLeft: `4px solid ${theme.primary}`
                        }}>
                          <strong>Explanation:</strong> {result.explanation}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              {!quizResults && (
                <>
                  <button
                    onClick={handleQuizSubmit}
                    disabled={submittingQuiz || quizAnswers.some(a => a === null)}
                    style={{
                      flex: 1,
                      padding: '16px',
                      backgroundColor: (submittingQuiz || quizAnswers.some(a => a === null)) ? theme.border : theme.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: (submittingQuiz || quizAnswers.some(a => a === null)) ? 'not-allowed' : 'pointer',
                      opacity: (submittingQuiz || quizAnswers.some(a => a === null)) ? 0.6 : 1,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!submittingQuiz && !quizAnswers.some(a => a === null)) {
                        e.currentTarget.style.backgroundColor = '#3182ce';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!submittingQuiz && !quizAnswers.some(a => a === null)) {
                        e.currentTarget.style.backgroundColor = theme.primary;
                      }
                    }}
                  >
                    {submittingQuiz ? 'Submitting...' : 'Submit Quiz'}
                  </button>
                  
                  {(quizMode || 'perStep') === 'atEnd' && (
                    <button
                      onClick={() => {
                        setShowQuizModal(false);
                        setQuizAnswers([]);
                        setQuizResults(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '16px',
                        backgroundColor: theme.border,
                        color: theme.textSecondary,
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.borderLight;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme.border;
                      }}
                    >
                      Take Later ⏰
                    </button>
                  )}
                </>
              )}

              {quizResults && !quizResults.passed && (
                <button
                  onClick={handleRetryQuiz}
                  style={{
                    flex: 1,
                    padding: '16px',
                    backgroundColor: theme.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#3182ce';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.primary;
                  }}
                >
                  Try Again
                </button>
              )}

              {quizResults && quizResults.passed && (
                <button
                  onClick={handleCloseQuizModal}
                  style={{
                    flex: 1,
                    padding: '16px',
                    backgroundColor: theme.success,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#38a169';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.success;
                  }}
                >
                  {(quizResults.assignmentCompleted && quizResults.remainingQuizzes === 0) ? 'View Summary 🎉' : 'Continue →'}
                </button>
              )}
            </div>
        </Box>
      </Modal>

      {/* Quiz Mode Selection Modal */}
      {showQuizModeModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowQuizModeModal(false)}
        >
          <div
            style={{
              backgroundColor: theme.surface,
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
              transition: 'all 0.3s'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                🧠
              </div>
              <h3 style={{
                color: theme.text,
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                Quiz Preference
              </h3>
              <p style={{
                color: theme.textSecondary,
                fontSize: '15px'
              }}>
                How would you like to take the quizzes for this assignment?
              </p>
            </div>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              {/* Per Step Option */}
              <button
                onClick={() => {
                  setQuizMode('perStep');
                  setShowQuizModeModal(false);
                  setAssignmentStarted(true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  padding: '20px',
                  backgroundColor: theme.surface,
                  border: `2px solid ${theme.primary}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? '#2d3748' : '#edf2f7';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.surface;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ fontSize: '32px', marginTop: '-4px' }}>
                    ⚡
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: theme.text, fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                      After Each Step
                    </div>
                    <div style={{ color: theme.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                      Take and pass the quiz after completing each step. You must pass the quiz to proceed to the next step.
                    </div>
                    <div style={{ 
                      color: theme.primary, 
                      fontSize: '13px', 
                      marginTop: '8px',
                      fontWeight: '600'
                    }}>
                      ✓ Reinforces learning immediately
                    </div>
                  </div>
                </div>
              </button>

              {/* At End Option */}
              <button
                onClick={() => {
                  setQuizMode('atEnd');
                  setShowQuizModeModal(false);
                  setAssignmentStarted(true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  padding: '20px',
                  backgroundColor: theme.surface,
                  border: `2px solid ${theme.success}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? '#2d3748' : '#edf2f7';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.surface;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ fontSize: '32px', marginTop: '-4px' }}>
                    ⏰
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: theme.text, fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                      All at the End
                    </div>
                    <div style={{ color: theme.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                      Complete all coding steps first, then take all quizzes together. You must pass all quizzes to complete the assignment.
                    </div>
                    <div style={{ 
                      color: theme.success, 
                      fontSize: '13px', 
                      marginTop: '8px',
                      fontWeight: '600'
                    }}>
                      ✓ Focus on coding first, then test knowledge
                    </div>
                  </div>
                </div>
              </button>
            </div>
            {/* Info */}
            <div style={{
              backgroundColor: darkMode ? '#2d3748' : '#f7fafc',
              padding: '16px',
              borderRadius: '8px',
              border: `1px solid ${theme.border}`
            }}>
              <p style={{
                color: theme.textMuted,
                fontSize: '13px',
                lineHeight: '1.6',
                margin: 0
              }}>
                💡 <strong>Note:</strong> Both options require passing all quizzes to complete the assignment. Choose the approach that works best for your learning style.
              </p>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TakeCodingAssignment;
